import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, FileText, Loader2, Music, Paperclip } from "@fg/icons";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";

interface ChannelAttachment {
    id: string;
    messageId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
}

/**
 * Lists the attachments registered against a channel message and
 * renders Teams-style inline previews — images full-size in a
 * thumbnail strip, video / audio embedded as native browser players,
 * everything else as a download pill. Authenticated previews stream
 * the bytes via fetch + objectURL since the browser can't attach a
 * bearer header to a plain &lt;img src=&gt; request.
 */
export function AttachmentPreviews({
    channelId,
    messageId
}: {
    channelId: string;
    messageId: string;
}) {
    const token = useAccessToken();
    const list = useQuery({
        queryKey: ["channel-attachments", channelId, messageId, token],
        queryFn: () => connectParityApi.listChannelAttachments(channelId, messageId, token),
        enabled: Boolean(token),
        staleTime: 60_000
    });

    const items = (list.data ?? []) as ChannelAttachment[];
    if (items.length === 0) return null;

    return (
        <div className="mt-1 flex flex-col gap-2">
            {items.map((a) => (
                <AttachmentTile key={a.id} channelId={channelId} attachment={a} />
            ))}
        </div>
    );
}

function AttachmentTile({
    channelId,
    attachment
}: {
    channelId: string;
    attachment: ChannelAttachment;
}) {
    const token = useAccessToken();
    const kind = classify(attachment.contentType, attachment.fileName);
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Eagerly fetch + objectURL only for inline-renderable kinds. Others
    // get a download button so we don't burn memory on bulk PDF blobs.
    useEffect(() => {
        if (kind === "file" || kind === "pdf") return;
        let cancelled = false;
        let url: string | null = null;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const baseUrl = import.meta.env.VITE_CONNECT_BFF_BASE_URL ?? "http://localhost:5104";
                // Images fetch the BFF-side thumbnail (320 px JPEG; ~30 KB
                // for typical photos) instead of the multi-MB original.
                // The /thumbnail endpoint falls back to the original on
                // 404, so pre-thumbnail-pipeline attachments still render.
                // Video / audio still need the full bytes for native
                // playback, so they hit /download.
                const path = kind === "image"
                    ? `attachments/${attachment.id}/thumbnail`
                    : `attachments/${attachment.id}/download`;
                const res = await fetch(
                    `${baseUrl}/api/channels/${channelId}/messages/${attachment.messageId}/${path}`,
                    {
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                        credentials: "omit"
                    });
                if (!res.ok) throw new Error(`${res.status}`);
                const blob = await res.blob();
                url = URL.createObjectURL(blob);
                if (!cancelled) setObjectUrl(url);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [channelId, attachment.id, attachment.messageId, kind, token]);

    if (kind === "image") {
        return (
            <figure className="overflow-hidden rounded-md border bg-muted/30">
                {loading ? (
                    <div className="flex h-32 items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                ) : objectUrl ? (
                    <img
                        src={objectUrl}
                        alt={attachment.fileName}
                        className="max-h-80 w-auto object-contain"
                    />
                ) : (
                    <FilePill attachment={attachment} error={error} />
                )}
                <figcaption className="border-t bg-background/80 px-2 py-1 text-[10px] text-muted-foreground">
                    {attachment.fileName} · {formatBytes(attachment.sizeBytes)}
                </figcaption>
            </figure>
        );
    }

    if (kind === "video") {
        return (
            <div className="overflow-hidden rounded-md border bg-black">
                {loading ? (
                    <div className="flex h-40 items-center justify-center text-white/80">
                        <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                ) : objectUrl ? (
                    <video
                        src={objectUrl}
                        controls
                        preload="metadata"
                        className="max-h-96 w-full"
                    />
                ) : (
                    <FilePill attachment={attachment} error={error} />
                )}
                <div className="bg-background/80 px-2 py-1 text-[10px] text-muted-foreground">
                    {attachment.fileName} · {formatBytes(attachment.sizeBytes)}
                </div>
            </div>
        );
    }

    if (kind === "audio") {
        return (
            <div className="rounded-md border bg-muted/30 p-2">
                <div className="flex items-center gap-2 text-xs">
                    <Music className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate font-medium">{attachment.fileName}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground">
                        {formatBytes(attachment.sizeBytes)}
                    </span>
                </div>
                {objectUrl ? (
                    <audio src={objectUrl} controls preload="metadata" className="mt-1 w-full" />
                ) : loading ? (
                    <Loader2 className="mt-1 h-4 w-4 animate-spin text-muted-foreground" />
                ) : null}
            </div>
        );
    }

    return <FilePill attachment={attachment} error={error} />;
}

function FilePill({
    attachment,
    error
}: {
    attachment: ChannelAttachment;
    error?: string | null;
}) {
    return (
        <div className="inline-flex max-w-full items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs">
            <FileIcon contentType={attachment.contentType} fileName={attachment.fileName} />
            <div className="min-w-0">
                <div className="truncate font-medium">{attachment.fileName}</div>
                <div className="text-muted-foreground">
                    {formatBytes(attachment.sizeBytes)} · {attachment.contentType || "file"}
                </div>
                {error ? <div className="text-destructive">Couldn't load preview ({error})</div> : null}
            </div>
            <Download className="ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label="Download" />
        </div>
    );
}

function FileIcon({ contentType, fileName }: { contentType: string; fileName: string }) {
    if (contentType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf")) {
        return <FileText className="h-4 w-4 shrink-0 text-red-500" aria-hidden />;
    }
    return <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />;
}

function classify(contentType: string, fileName: string): "image" | "video" | "audio" | "pdf" | "file" {
    const ct = (contentType || "").toLowerCase();
    if (ct.startsWith("image/")) return "image";
    if (ct.startsWith("video/")) return "video";
    if (ct.startsWith("audio/")) return "audio";
    if (ct === "application/pdf") return "pdf";
    const ext = fileName.toLowerCase().split(".").pop() ?? "";
    if (["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"].includes(ext)) return "image";
    if (["mp4", "webm", "mov", "mkv"].includes(ext)) return "video";
    if (["mp3", "wav", "ogg", "flac", "m4a"].includes(ext)) return "audio";
    if (ext === "pdf") return "pdf";
    return "file";
}

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}
